/** Seeded system contents (M3 spec §3): star, bodies, fields, points of interest, reasons to visit, density rule. */
import { logger } from '../core/logger'
import { createRng, nextFloat, range, type Rng } from '../core/rng'
import { galaxyOres, galaxyReasons, type GalaxyConfig } from '../data/galaxyConfig'
import { systemId } from './placement'
import { deriveSeed, intRange, pickWeighted } from './seed'
import type { AnchorId, Archetype, Body, BodyKind, Field, Poi, PoiKind, ReasonToVisit, RegionId, Relay, StarSystem } from './types'

/** Debug message (with `{ index, attempt }`) for each density-rule re-roll. */
export const DENSITY_RETRY = 'galaxy: density rule re-rolled a system'
/** Warning (with `{ index, added }`) when the retries ran out and fallback POIs were forced in. */
export const DENSITY_FORCED = 'galaxy: density rule forced fallback POIs'

type ArchetypeCfg = GalaxyConfig['archetypes'][Archetype]
type AnchorCfg = GalaxyConfig['anchors'][AnchorId]
type FieldKind = Field['kind']

/** Archetype weights for a region; the chokepoint weight rises for systems on a cross-region relay. */
export function archetypeWeightsFor(region: RegionId, onBorder: boolean, cfg: GalaxyConfig): Record<Archetype, number> {
  const base = cfg.archetypeWeights[region]
  return onBorder ? { ...base, chokepoint: base.chokepoint + cfg.chokepointBonus } : base
}

/**
 * Generates one system from `deriveSeed(galaxySeed, 'system', index)`. `relays` are the relays touching this
 * system. The name is left empty; `generateGalaxy` names systems once all of them exist.
 * Density rule: a system with too few POIs or no reason to visit is re-rolled with
 * `deriveSeed(galaxySeed, 'system', index, 'retry', n)`, then fallback POIs are forced in.
 * `forcedArchetype` (the vein guarantee, D-086) replaces the rolled archetype after the roll, so the RNG stream
 * and every later roll are exactly what the system would get had it rolled that archetype. Anchors ignore it.
 */
export function generateSystem(
  galaxySeed: number,
  index: number,
  region: RegionId,
  mapPos: readonly [number, number, number],
  relays: readonly Relay[],
  anchor: AnchorId | null,
  cfg: GalaxyConfig,
  forcedArchetype: Archetype | null = null,
): StarSystem {
  const d = cfg.density
  const dense = (s: StarSystem): boolean => s.pois.length >= d.minPois && s.reasons.length >= d.minReasons
  const roll = (seed: number): StarSystem => rollSystem(createRng(seed), index, region, mapPos, relays, anchor, forcedArchetype, cfg)
  let sys = roll(deriveSeed(galaxySeed, 'system', index))
  for (let n = 1; n <= d.maxRetries && !dense(sys); n++) {
    logger.debug(DENSITY_RETRY, { index, attempt: n })
    sys = roll(deriveSeed(galaxySeed, 'system', index, 'retry', n))
  }
  return dense(sys) ? sys : forceDensity(createRng(deriveSeed(galaxySeed, 'system', index, 'forced')), sys, anchor, cfg)
}

function rollSystem(
  rng: Rng,
  index: number,
  region: RegionId,
  mapPos: readonly [number, number, number],
  relays: readonly Relay[],
  anchor: AnchorId | null,
  forced: Archetype | null,
  cfg: GalaxyConfig,
): StarSystem {
  const id = systemId(index)
  const starClass = pickWeighted(rng, weightsOf(cfg.stars))
  const { color, luminosity } = cfg.stars[starClass]
  const onBorder = relays.some((r) => r.crossRegion && !r.long)
  const rolled = pickWeighted(rng, archetypeWeightsFor(region, onBorder, cfg))
  const anchorCfg = anchor ? cfg.anchors[anchor] : null
  const archetype = anchorCfg ? (anchorCfg.archetype ?? rolled) : (forced ?? rolled)
  const arch = cfg.archetypes[archetype]
  const rolled0 = rollBodies(rng, id, arch, luminosity, cfg)
  const fields = rollFields(rng, id, arch, anchorCfg, cfg)
  const bodies = anchorCfg?.hero && fields.length > 0 ? withHero(rolled0, id, fields[0], anchorCfg.hero, luminosity, cfg) : rolled0
  const pois = rollPois(rng, id, arch, anchorCfg, cfg)
  return {
    id,
    index,
    name: '',
    region,
    mapPos: [mapPos[0], mapPos[1], mapPos[2]],
    star: { class: starClass, color, luminosity },
    archetype,
    anchor,
    bodies,
    fields,
    pois,
    reasons: deriveReasons(arch, anchorCfg, pois, cfg),
    relayIds: relays.map((r) => r.id),
  }
}

function weightsOf<K extends string>(table: Record<K, { weight: number }>): Record<K, number> {
  const out = {} as Record<K, number>
  for (const k of Object.keys(table) as K[]) out[k] = table[k].weight
  return out
}

/**
 * Titius–Bode-like orbits (D-TBD inner orbits): the innermost at `firstOrbitM` × √luminosity, each next × an
 * `orbitRatio` draw. When the outermost would pass the band, the log offsets are compressed so it lands on the band's
 * edge; then a forward pass keeps `minOrbitGapM` and a backward pass keeps the band (the config check guarantees both fit).
 */
export function rollOrbits(rng: Rng, n: number, luminosity: number, b: GalaxyConfig['bodies']): number[] {
  const [lo, hi] = b.orbitM
  const clamp = (x: number): number => Math.min(hi, Math.max(lo, x))
  const first = clamp(range(rng, b.firstOrbitM[0], b.firstOrbitM[1]) * Math.sqrt(luminosity))
  const orbits = [first]
  for (let i = 1; i < n; i++) orbits.push(orbits[i - 1] * range(rng, b.orbitRatio[0], b.orbitRatio[1]))
  const last = orbits[n - 1]
  if (last > hi) {
    const k = Math.log(hi / first) / Math.log(last / first)
    for (let i = 1; i < n; i++) orbits[i] = first * Math.pow(orbits[i] / first, k)
  }
  for (let i = 1; i < n; i++) orbits[i] = Math.max(orbits[i], orbits[i - 1] + b.minOrbitGapM)
  orbits[n - 1] = Math.min(orbits[n - 1], hi)
  for (let i = n - 2; i >= 0; i--) orbits[i] = Math.min(orbits[i], orbits[i + 1] - b.minOrbitGapM)
  return orbits
}

/** Orbits from `rollOrbits`; gas giants take the outermost slots; lava leans inside and ice outside (log position). */
function rollBodies(rng: Rng, id: string, arch: ArchetypeCfg, luminosity: number, cfg: GalaxyConfig): Body[] {
  const b = cfg.bodies
  const n = intRange(rng, b.count[0], b.count[1])
  const orbits = rollOrbits(rng, n, luminosity, b)
  const gasCount = orbits.filter(() => nextFloat(rng) < b.gasChance).length
  const inner = Math.max(b.orbitM[0], b.firstOrbitM[0] * Math.sqrt(luminosity))
  const span = Math.log(b.orbitM[1] / inner)
  const kinds = orbits.map((orbit, i): BodyKind => {
    if (i >= n - gasCount) return 'gas'
    const t = span > 0 ? Math.min(1, Math.max(0, Math.log(orbit / inner) / span)) : 0 // 0 inside, 1 at the band's edge
    return pickWeighted(rng, { rocky: b.otherKinds.rocky, icy: b.otherKinds.icy * 2 * t, lava: b.otherKinds.lava * 2 * (1 - t) })
  })
  // An archetype that needs gas giants converts the outermost rocky bodies.
  for (let i = n - 1, missing = (arch.minGasGiants ?? 0) - kinds.filter((k) => k === 'gas').length; i >= 0 && missing > 0; i--) {
    if (kinds[i] === 'gas') continue
    kinds[i] = 'gas'
    missing--
  }
  const bodies = orbits.map((orbitRadiusM, i): Body => {
    const gas = kinds[i] === 'gas'
    const [rLo, rHi] = gas ? b.gasRadiusM : b.otherRadiusM
    const [pLo, pHi] = gas ? b.gasRotationS : b.otherRotationS
    return {
      id: `${id}-b${i}`,
      kind: kinds[i],
      orbitRadiusM,
      angleRad: range(rng, 0, Math.PI * 2),
      radiusM: range(rng, rLo, rHi),
      rings: gas && nextFloat(rng) < b.ringChance,
      rotationPeriodS: range(rng, pLo, pHi),
    }
  })
  return spreadByExtent(bodies, b)
}

/** A body's outer extent (m): its rings, else its atmosphere halo. */
export function bodyExtentM(body: Pick<Body, 'radiusM' | 'rings'>, b: GalaxyConfig['bodies']): number {
  return body.radiusM * (body.rings ? b.ringExtentFactor : b.atmosphereExtentFactor)
}

/**
 * Game-scale bodies (D-TBD game-scale planets): neighbouring orbits keep their outer extents plus `extentMarginM`
 * apart (a forward push), then the band's edge holds (a backward pull; the config check guarantees both fit).
 */
function spreadByExtent(bodies: Body[], b: GalaxyConfig['bodies']): Body[] {
  const n = bodies.length
  const gap = (i: number): number => Math.max(b.minOrbitGapM, bodyExtentM(bodies[i - 1], b) + bodyExtentM(bodies[i], b) + b.extentMarginM)
  const orbits = bodies.map((x) => x.orbitRadiusM)
  for (let i = 1; i < n; i++) orbits[i] = Math.max(orbits[i], orbits[i - 1] + gap(i))
  orbits[n - 1] = Math.min(orbits[n - 1], b.orbitM[1])
  for (let i = n - 2; i >= 0; i--) orbits[i] = Math.min(orbits[i], orbits[i + 1] - gap(i + 1))
  return bodies.map((x, i) => (orbits[i] === x.orbitRadiusM ? x : { ...x, orbitRadiusM: orbits[i] }))
}

type HeroCfg = NonNullable<AnchorCfg['hero']>

/** Generator-frame position of a body. */
function bodyPos(x: Body): [number, number, number] {
  return [x.orbitRadiusM * Math.cos(x.angleRad), x.orbitRadiusM * Math.sin(x.angleRad), x.heightM ?? 0]
}

/**
 * The home hero (D-TBD game-scale planets): a guaranteed body placed from the home field's centre along the hero
 * direction (the first `yawTryDeg` step that clears the star), at the distance where its disc spans
 * `angularDiameterDeg`. Rolled bodies that would touch it or the home field are turned along their orbits. No RNG
 * draws, so every other roll is unchanged.
 */
export function withHero(bodies: readonly Body[], id: string, home: Field, h: HeroCfg, luminosity: number, cfg: GalaxyConfig): Body[] {
  const b = cfg.bodies
  const dist = h.radiusM / Math.tan((h.angularDiameterDeg * Math.PI) / 360)
  const ext = h.radiusM * (h.rings ? b.ringExtentFactor : b.atmosphereExtentFactor)
  const starClear = b.heroStarClearanceFactor * b.firstOrbitM[0] * Math.sqrt(luminosity) + ext
  const [dx, dy, dz] = h.direction
  const dl = Math.hypot(dx, dy, dz)
  const c = home.center
  let best: [number, number, number] = [c[0], c[1], c[2]]
  let bestR = -1
  for (const yawDeg of h.yawTryDeg) {
    const a = (yawDeg * Math.PI) / 180
    const x = (dx * Math.cos(a) - dy * Math.sin(a)) / dl
    const y = (dx * Math.sin(a) + dy * Math.cos(a)) / dl
    const p: [number, number, number] = [c[0] + x * dist, c[1] + y * dist, c[2] + (dz / dl) * dist]
    const r = Math.hypot(p[0], p[1])
    if (r >= starClear) {
      best = p
      break
    }
    if (r > bestR) {
      best = p
      bestR = r
    }
  }
  const hero: Body = {
    id: `${id}-b${bodies.length}`,
    kind: h.kind,
    orbitRadiusM: Math.hypot(best[0], best[1]),
    angleRad: Math.atan2(best[1], best[0]),
    heightM: best[2],
    radiusM: h.radiusM,
    rings: h.rings,
    rotationPeriodS: h.rotationS,
  }
  // Keep-outs: the hero (its extent) and the home field (its radius).
  const keep: [readonly [number, number, number], number][] = [
    [best, ext],
    [c, home.radiusM],
  ]
  const clear = (x: Body): boolean => {
    const p = bodyPos(x)
    return keep.every(([q, r]) => Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]) >= r + bodyExtentM(x, b) + b.extentMarginM)
  }
  const moved = bodies.map((x) => {
    let y = x
    // Turn along the orbit in steps of about the needed clearance until clear (at most one full turn).
    const step = Math.min(Math.PI / 8, (ext * 2 + bodyExtentM(x, b) * 2 + b.extentMarginM) / x.orbitRadiusM)
    for (let k = 1; !clear(y) && k * step < Math.PI * 2; k++) y = { ...x, angleRad: x.angleRad + k * step }
    return y
  })
  return [...moved, hero]
}

/** lo + (hi − lo) × u^bias: a bias above 1 pulls the radius toward the star (D-TBD inner orbits). */
function biasedRadius(rng: Rng, [lo, hi]: readonly [number, number], bias: number): number {
  return lo + (hi - lo) * Math.pow(nextFloat(rng), bias)
}

function rollFields(rng: Rng, id: string, arch: ArchetypeCfg, anchorCfg: AnchorCfg | null, cfg: GalaxyConfig): Field[] {
  const f = cfg.fields
  const min = arch.minFields ?? {}
  const minIce = min.ice ?? 0
  const minGas = min.gas ?? 0
  const asteroids = Math.max(intRange(rng, f.asteroid[0], f.asteroid[1]), min.asteroid ?? 0)
  const iceGas = Math.max(intRange(rng, f.iceGas[0], f.iceGas[1]), minIce + minGas)
  const kinds: FieldKind[] = [
    ...Array.from({ length: asteroids }, (): FieldKind => 'asteroid'),
    ...Array.from({ length: minIce }, (): FieldKind => 'ice'),
    ...Array.from({ length: minGas }, (): FieldKind => 'gas'),
    ...Array.from({ length: iceGas - minIce - minGas }, (): FieldKind => pickWeighted(rng, f.iceGasKinds)),
  ]
  const fields = kinds.map((kind, i): Field => {
    const r = biasedRadius(rng, f.orbitM, f.radialBias)
    const angle = range(rng, 0, Math.PI * 2)
    return {
      id: `${id}-f${i}`,
      kind,
      center: [r * Math.cos(angle), r * Math.sin(angle), range(rng, -f.zM, f.zM)],
      radiusM: range(rng, f.radiusM[0], f.radiusM[1]),
      oreWeights: oreWeightsFor(kind, arch, cfg),
    }
  })
  const trace = anchorCfg?.trace
  if (!trace) return fields
  // Asteroid fields come first, so the trace goes into the first n of them (there is always at least one).
  const laced = Math.min(intRange(rng, trace.fields[0], trace.fields[1]), asteroids)
  return fields.map((field, i) => (i < laced ? { ...field, oreWeights: withTrace(field.oreWeights, trace.ore, trace.weight) } : field))
}

/** Gives `ore` a fixed share `weight` of the field and scales every other ore down to fit (still sums to 1). */
function withTrace(weights: Record<string, number>, ore: string, weight: number): Record<string, number> {
  const out: Record<string, number> = {}
  for (const [k, w] of Object.entries(weights)) if (k !== ore) out[k] = w * (1 - weight)
  out[ore] = weight + (weights[ore] ?? 0) * (1 - weight)
  return out
}

/** Base ores for the field kind × the archetype's multipliers, + its additions (asteroid fields only), normalized. */
function oreWeightsFor(kind: FieldKind, arch: ArchetypeCfg, cfg: GalaxyConfig): Record<string, number> {
  const base = cfg.fields.ores[kind]
  const raw = galaxyOres.map((ore) => {
    const w = (base[ore] ?? 0) * (arch.oreMult?.[ore] ?? 1) + (kind === 'asteroid' ? (arch.oreAdd?.[ore] ?? 0) : 0)
    return [ore, w] as const
  })
  const total = raw.reduce((s, [, w]) => s + w, 0)
  const out: Record<string, number> = {}
  for (const [ore, w] of raw) if (w > 0) out[ore] = w / total
  return out
}

function rollPois(rng: Rng, id: string, arch: ArchetypeCfg, anchorCfg: AnchorCfg | null, cfg: GalaxyConfig): Poi[] {
  const p = cfg.pois
  const weights = { ...p.weights }
  for (const k of Object.keys(weights) as PoiKind[]) weights[k] *= arch.poiMult?.[k] ?? 1
  const kinds: PoiKind[] = Array.from({ length: intRange(rng, p.count[0], p.count[1]) }, () => pickWeighted(rng, weights))
  for (const extras of [arch.extraPois, anchorCfg?.extraPois]) {
    for (const [kind, n] of Object.entries(extras ?? {}) as [PoiKind, number][]) {
      for (let i = 0; i < n; i++) kinds.push(kind)
    }
  }
  return kinds.map((kind, i) => makePoi(rng, `${id}-poi-${i}`, kind, cfg))
}

function makePoi(rng: Rng, id: string, kind: PoiKind, cfg: GalaxyConfig): Poi {
  const p = cfg.pois
  const r = biasedRadius(rng, p.radiusM, p.radialBias)
  const angle = range(rng, 0, Math.PI * 2)
  return {
    id,
    kind,
    position: [r * Math.cos(angle), r * Math.sin(angle), range(rng, -p.zM, p.zM)],
    hidden: kind === 'hidden-base' || nextFloat(rng) < p.hiddenChance[kind],
  }
}

function deriveReasons(arch: ArchetypeCfg, anchorCfg: AnchorCfg | null, pois: readonly Poi[], cfg: GalaxyConfig): ReasonToVisit[] {
  const set = new Set<ReasonToVisit>([...arch.reasons, ...(anchorCfg?.reasons ?? [])])
  for (const poi of pois) {
    const r = cfg.pois.reasons[poi.kind]
    if (r) set.add(r)
  }
  return galaxyReasons.filter((r) => set.has(r))
}

/** Last resort after the retries: top up with fallback POIs (which carry a reason to visit), then log it. */
function forceDensity(rng: Rng, sys: StarSystem, anchor: AnchorId | null, cfg: GalaxyConfig): StarSystem {
  const d = cfg.density
  const pois = [...sys.pois]
  const add = (): void => {
    pois.push(makePoi(rng, `${sys.id}-poi-${pois.length}`, d.fallbackPoi, cfg))
  }
  while (pois.length < d.minPois) add()
  const reasonsFor = (): ReasonToVisit[] => deriveReasons(cfg.archetypes[sys.archetype], anchor ? cfg.anchors[anchor] : null, pois, cfg)
  if (reasonsFor().length < d.minReasons) add()
  logger.warn(DENSITY_FORCED, { index: sys.index, added: pois.length - sys.pois.length })
  return { ...sys, pois, reasons: reasonsFor() }
}
